import { useState, useRef } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, isColorDark, getAnimation, hexToRgba, resolveLinkProps, resolveVideo } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function TestimonialVideoSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'grid';

    const items = content.items || [];
    const [activeVideo, setActiveVideo] = useState(null);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const scrollRef = useRef(null);

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const cardBg = s.dark ? 'rgba(255,255,255,0.06)' : '#ffffff';
    const cardBorder = s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';

    const resolveItemVideo = (item) => resolveVideo(item, 'video', mediaFolders);

    const resolveThumb = (item) => {
        if (item.image_folder_id && item.image_file_id && mediaFolders[item.image_folder_id]) {
            const f = mediaFolders[item.image_folder_id].find(x => String(x.id) === String(item.image_file_id));
            if (f) return f.image_url;
        }
        if (item.image_url) return item.image_url;
        const vid = resolveItemVideo(item);
        return vid?.thumb || null;
    };

    const VideoModal = () => {
        if (activeVideo === null) return null;
        const item = items[activeVideo];
        const vid = resolveItemVideo(item);
        if (!vid) return null;
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 md:p-8"
                onClick={() => setActiveVideo(null)}>
                <div className="relative w-full max-w-4xl" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setActiveVideo(null)}
                        className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div className="aspect-video rounded-2xl overflow-hidden bg-black">
                        {vid.type === 'file'
                            ? <video src={vid.src} className="w-full h-full" controls autoPlay />
                            : <iframe src={vid.embed} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen title={item.name || 'Video testimonial'} />
                        }
                    </div>
                </div>
            </div>
        );
    };

    const PlayButton = ({ size = 'md' }) => {
        const sizeClass = size === 'lg' ? 'w-16 h-16 md:w-20 md:h-20' : 'w-12 h-12 md:w-14 md:h-14';
        const iconClass = size === 'lg' ? 'w-7 h-7 md:w-8 md:h-8' : 'w-5 h-5 md:w-6 md:h-6';
        return (
            <div className={`${sizeClass} rounded-full flex items-center justify-center text-white transition-all duration-300 group-hover:scale-110`}
                style={{ backgroundColor: s.accent, boxShadow: `0 0 0 6px ${hexToRgba(s.accent, 0.2)}` }}>
                <svg className={`${iconClass} ml-0.5`} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
            </div>
        );
    };

    const VideoCard = ({ item, index, large = false }) => {
        const thumb = resolveThumb(item);
        const aspectClass = large ? 'aspect-video' : 'aspect-[4/3]';
        return (
            <div className={`rounded-2xl border overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ backgroundColor: cardBg, borderColor: cardBorder, transitionDelay: visible ? `${index * 100}ms` : '0ms' }}>
                <div className={`relative ${aspectClass} bg-gray-900 cursor-pointer group`} onClick={() => setActiveVideo(index)}>
                    {thumb && <img src={thumb} alt={item.name || ''} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <PlayButton size={large ? 'lg' : 'md'} />
                    </div>
                </div>
                <div className="p-4 md:p-5">
                    {item.quote && <p className="text-sm italic leading-relaxed mb-3" style={{ color: s.textSub }}>"{item.quote}"</p>}
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ backgroundColor: s.accent }}>
                            {(item.name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                            <div className="text-sm font-semibold" style={{ color: s.text }}>{item.name}</div>
                            {(item.role || item.company) && (
                                <div className="text-xs" style={{ color: s.textSub }}>
                                    {item.role}{item.role && item.company ? ' - ' : ''}{item.company}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const carouselPrev = () => setCarouselIndex(Math.max(0, carouselIndex - 1));
    const carouselNext = () => setCarouselIndex(Math.min(items.length - 1, carouselIndex + 1));

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                {variant === 'featured' ? (
                    <div>
                        {/* Featured large card */}
                        <div className="mb-6">
                            <VideoCard item={items[0]} index={0} large />
                        </div>
                        {/* Thumbnails row */}
                        {items.length > 1 && (
                            <div className={`grid grid-cols-2 ${items.length > 3 ? 'md:grid-cols-4' : items.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
                                {items.slice(1).map((item, i) => (
                                    <VideoCard key={i + 1} item={item} index={i + 1} />
                                ))}
                            </div>
                        )}
                    </div>
                ) : variant === 'carousel' ? (
                    <div className="relative">
                        <div className="overflow-hidden" ref={scrollRef}>
                            <div className="flex transition-transform duration-500 ease-out gap-6"
                                style={{ transform: `translateX(-${carouselIndex * (100 / Math.min(items.length, 3))}%)` }}>
                                {items.map((item, i) => (
                                    <div key={i} className="w-full md:w-[calc(33.333%-16px)] flex-shrink-0">
                                        <VideoCard item={item} index={i} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        {items.length > 1 && (
                            <div className="flex items-center justify-center gap-3 mt-6">
                                <button onClick={carouselPrev} disabled={carouselIndex === 0}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${carouselIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:scale-110'}`}
                                    style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                                    <svg className="w-5 h-5" style={{ color: s.text }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                </button>
                                <div className="flex gap-1.5">
                                    {items.map((_, i) => (
                                        <button key={i} onClick={() => setCarouselIndex(i)}
                                            className="w-2 h-2 rounded-full transition-all duration-300"
                                            style={{ backgroundColor: i === carouselIndex ? s.accent : (s.dark ? 'rgba(255,255,255,0.2)' : '#d1d5db'), transform: i === carouselIndex ? 'scale(1.3)' : 'scale(1)' }} />
                                    ))}
                                </div>
                                <button onClick={carouselNext} disabled={carouselIndex >= items.length - 1}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${carouselIndex >= items.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:scale-110'}`}
                                    style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                                    <svg className="w-5 h-5" style={{ color: s.text }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    /* grid (default) */
                    <div className={`grid grid-cols-1 ${items.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
                        {items.map((item, i) => (
                            <VideoCard key={i} item={item} index={i} />
                        ))}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            <VideoModal />
        </section>
    );
}
